/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import get from 'lodash/get';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';

import { errorWithLevelWarning } from '../error/error-with-level';
import { fetchWithTimeout } from '../fetch/fetch';
import SetupError from './setup-error';

/**
 * Struktura organizací vrácená serverem při registračním procesu.
 * @typedef {Object} Organization
 * @property {number} id Číselný identifikátor organizace.
 * @property {string} name Název organizace.
 * @property {Organization[]} [child] Podřízené organizace.
 *
 * Volitelné a přítomné jen pro účely parsování hierarchické struktury
 * z Ki-Wi Serveru, aby nemusely být dva typy, jinak typicky nevyplněno.
 */

// Pomocné funkce pro registraci zařízení na vzdáleném serveru.
/**
 * Zjistí seznam organizací na serveru, do kterých lze zařízení přiřadit.
 * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
 * @param {string} username Jméno uživatele na serveru.
 * @param {string} password Heslo uživatele na serveru.
 * @returns {Promise<Organization[], SetupError>} Vyřeší se seznamem organizací
 * nebo odmítne pomocí SetupError, pokud se při registraci vyskytla chyba.
 */
export async function fetchOrganizations(serverUrl, username, password) {
  return await handleFetch(
    // Odešle se požadavek s přihlašovacími údaji na server.
    fetchWithTimeout(`${serverUrl}/api/register/verify`, {
      body: JSON.stringify({ username, password }),
      headers: { 'Content-type': 'application/json' },
      method: 'POST'
    }),
    serverUrl,
    parseOrganizations
  );
}

/**
 * Převede stromovou strukturu organizací na plochý seznam.
 *
 * Nevyplňuje volitelnou položku `child` na výstupu.
 * @param {Organization} organization Kořenová organizace, ve formátu, který poslal server.
 * @returns {Organization[]} Seznam organizací v pořadí pre-order.
 */
function flattenOrganization(organization) {
  const { child = [] } = organization;
  return child.reduce(
    (orgArray, childOrganization) =>
      orgArray.concat(flattenOrganization(childOrganization)),
    [pick(organization, 'id', 'name')]
  );
}

/**
 * Zjistí URL adresu pro registraci zařízení na serveru pomocí QR kódu.
 * Adresa se má otevřít v prohlížeči přes které se provede vytvoření zařízení.
 * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
 * @param {string} fingerprint Jedinečný otisk zařízení, který se má zaregistrovat.
 * @returns {string} URL pro manuální registraci.
 */
export function getQrUrl(serverUrl, fingerprint) {
  return `${serverUrl}/api/register/url/lighter/${fingerprint}`;
}

/**
 * @template T
 * @param {Promise<Response>} fetchPromise
 * @param {string} serverUrl
 * @param {(json: string) => T} [processJson]
 * @returns {Promise<T, SetupError>}
 */
async function handleFetch(fetchPromise, serverUrl, processJson) {
  /** @type {Response | undefined} */
  let response;
  /** @type {string | undefined} */
  let body;

  try {
    response = await fetchPromise;
    // Tělo načteme vždy, protože ho budeme potřebovat i pro ošetření
    // případného neúspěchu.
    body = await response.text();
    if (response.ok) {
      // Pokud je odpověď v pořádku, pokusíme se ji zpracovat jako JSON.
      const json = JSON.parse(body);
      return processJson ? processJson(json) : json;
    }
  } catch (cause) {
    // S případnou chybou (ať už z fetch, JSON.parse nebo z processJson)
    // nyní nic neděláme, proto ji nyní ani nezachytáváme. Vyhodíme obecnou
    // hlášku alespoň se serverUrl.
    // TODO: Přidat zachycenou chybu jako cause do SetupError.
    throw new SetupError(`Cannot connect to the server ${serverUrl}.`, {
      cause,
      response
    });
  }

  // Jakákoliv jiná odpověď serveru se považuje za chybu.
  // Vytvoří se specifická chyba s dodatečnými informacemi,
  // které server nahlásil.
  const message =
    parseErrorTypeToMessage(body) ??
    `HTTP status ${response.status}: ${response.statusText}.`;
  throw new SetupError(message, { response });
}

/**
 * Zjistí identifikátor zařízení z úspěšné registrace.
 * @param {Object} json Tělo odpovědi úspěšné registrace jako JSON objekt.
 * @returns {string}
 */
function parseDeviceId(json) {
  return json.device.deviceId;
}

/**
 * Zjistí typ chyby registrace a převede ho na čitelnou zprávu.
 * @param {string} text Tělo odpovědi chybné registrace jako prostý text.
 * @returns {string | undefined} Zpráva dle typu chyby nebo `undefined` pro neznámou.
 */
function parseErrorTypeToMessage(text) {
  switch (text) {
    case 'device.alreadyExists':
      return 'Device ID already exists.';
    case 'device.alreadyRegistered':
      return 'Device is already registered.';
    case 'device.missingAccessCode':
      return 'Missing device access code.';
    case 'device.saveError':
      return 'Unable to save the device.';
    case 'organization.deviceLimitExceeded':
      return 'License limit for number of devices was exceeded.';
    case 'organization.invalid':
      return 'Unable to add the device to organization.';
    case 'organization.missingOrganization':
      return 'Missing device organization.';
    case 'request.invalid':
      return 'Invalid request format.';
    case 'user.invalid':
      return 'Invalid username or password.';
    default:
  }
}

/**
 * Zjistí seznam organizací z úspěšné odpovědi.
 * @param {Object} json Tělo odpovědi úspěšně stažených organizací jako JSON objekt.
 * @returns {Organization[]}
 */
function parseOrganizations(json) {
  // Kořenová organizace vrácené struktury.
  // Pokud neexistuje, vrátíme prázdný seznam.
  const root = get(json, 'organization');
  if (isNil(root)) {
    return [];
  }
  // Ze stromové struktury organizací uděláme plochý seznam.
  return flattenOrganization(root);
}

/**
 * Automaticky zaregistruje zařízení na vzdáleném serveru.
 * Stačí znát otisk ke kterému se nalezne vytvořené zařízení na serveru.
 * Zařízení se na serveru vytvoří např. pomocí odkazu z QR kódu.
 * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
 * @param {string} fingerprint Jedinečný otisk zařízení.
 * @returns {Promise<string | null, SetupError>} Vyřeší se identifikátorem,
 * který je danému zařízení přiřazen na serveru, nebo `null`, pokud zařízení není
 * registrované.
 *
 * Odmítne se pomocí SetupError, pokud se při registraci vyskytla chyba.
 */
export async function registerAutomatic(serverUrl, fingerprint) {
  return await handleFetch(
    fetchWithTimeout(`${serverUrl}/api/register/sync`, {
      body: JSON.stringify({ accessCode: fingerprint }),
      headers: { 'Content-type': 'application/json' },
      method: 'POST'
    }),
    serverUrl,
    parseDeviceId
  ).catch((error) => {
    // handleFetch zaručuje, že typ chyby bude vždy SetupError,
    // proto si můžeme dovolit toto neslušné přetypování.
    // Status 404 značí, že nebylo nalezeno zařízení s předaným otiskem.
    // Vrací se tedy null.
    if (/** @type {SetupError} */ (error).response?.status === 404) {
      return null;
    }
    // Jinak se vyhodí jako warning pro typickou chybu síťového spojení.
    throw errorWithLevelWarning(error);
  });
}

/**
 * Manuálně zaregistruje zařízení na vzdáleném serveru.
 * K tomu je potřeba znát uživatele a organizaci do které se má zařízení vytvořit.
 * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
 * @param {string} fingerprint Jedinečný otisk zařízení.
 * @param {string} username Jméno uživatele na serveru.
 * @param {string} password Heslo uživatele na serveru.
 * @param {string} deviceName Název zařízení na serveru.
 * @param {number} organizationId Identifikátor organizace, do které se má zařízení přiřadit.
 * @returns {Promise<string, SetupError>} Vyřeší se identifikátorem
 * právě registrovaného zařízení nebo odmítne pomocí SetupError,
 * pokud se při registraci vyskytla chyba.
 */
export async function registerManual(
  serverUrl,
  fingerprint,
  username,
  password,
  deviceName,
  organizationId
) {
  return await handleFetch(
    fetchWithTimeout(`${serverUrl}/api/register/device`, {
      body: JSON.stringify({
        accessCode: fingerprint,
        deviceName,
        deviceType: 'lighter',
        organizationId,
        password,
        username
      }),
      headers: { 'Content-type': 'application/json' },
      method: 'POST'
    }),
    serverUrl,
    parseDeviceId
  );
}

export const internal = {
  handleFetch
};
